import { SpotlightManager } from '@atlaskit/onboarding';
import { useAnalytics, DatabaseContainerType } from '@databases/analytics';
import useCommentContext from '@databases/comments/hooks/useCommentContext';
import {
  getThreadObjectFromId,
  getThreadObjectFromReactionsId,
} from '@databases/comments/utils';
import SetupPanelListener from '@databases/database-setup/SetupPanelListener';
import EntryPanelLoader from '@databases/entry/EntryPanelLoader';
import CopyListener from '@databases/export/CopyListener';
import { useLocale } from '@databases/intl';
import useCloseViewPanelKeyboardShortcut from '@databases/keyboard-shortcuts/useCloseViewPanelKeyboardShortcut';
import DatabaseLayout from '@databases/layout/DatabaseLayout';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getValueId, isStandalone } from '@databases/utils/utils';
import FilteredValuesLoader from '@databases/views/FilteredValuesLoader';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { DatabaseActionKey } from '../database/DatabaseActions';

import DatabaseLayoutRenderer from './DatabaseLayoutRenderer';
import Toolbar from './Toolbar';

export interface DatabaseContentProps {
  contentId: string;
  isContentEditable?: boolean;
  isFieldsEditable?: boolean;
  actions?: DatabaseActionKey[];
}

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const DatabaseContent: FunctionComponent<DatabaseContentProps> = ({
  contentId,
  isContentEditable = false,
  isFieldsEditable = false,
  actions,
}) => {
  const analyticsClient = useAnalytics();
  const locale = useLocale();
  const dispatch = defaultStoreUtils.useDispatch();
  const { setOpenThread, focusedCommentContentId } = useCommentContext();

  const [outerContainerRef, setOuterContainerRef] = useState<HTMLElement | null>(null);

  const [operations, isLocked, comments] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    const comments = database.commentThreads;

    return [database.operations, database.meta.isLocked, comments] as const;
  });

  const canEditContent = isContentEditable && operations.updateEntries;
  const couldEditFields = isFieldsEditable && operations.updateMetadata;
  const canEditFields = couldEditFields && !isLocked;

  useEffect(() => {
    const attachCommentReopen = async () => {
      const bridge = await getBridgeAPI();
      if (!bridge) {
        return;
      }

      bridge.registerOnReopenResolvedComment?.((e) => {
        const commentThread = getThreadObjectFromId(e.referenceId, comments);
        if (
          commentThread?.fieldId &&
          commentThread.entryId &&
          commentThread.thread &&
          e.referenceId
        ) {
          const { fieldId, entryId } = commentThread;
          dispatch(
            addChangeToDraft(contentId, 'main', {
              type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED,
              entryId,
              fieldId,
              threadId: e.referenceId,
              isResolved: false,
            })
          );
          setOpenThread({
            valueId: getValueId(entryId, fieldId),
            threadId: commentThread.thread.threadId,
          });
        }
      });
    };
    if (isStandalone()) {
      attachCommentReopen();
    }

    if (focusedCommentContentId) {
      const commentThread = getThreadObjectFromReactionsId(
        focusedCommentContentId,
        comments
      );
      if (commentThread && commentThread.entryId && commentThread.fieldId) {
        const { fieldId, entryId } = commentThread;
        setOpenThread({
          valueId: getValueId(entryId, fieldId),
          threadId: commentThread.thread.threadId,
        });
      }
    }
  }, [comments, contentId, dispatch, focusedCommentContentId, setOpenThread]);

  useCallbackEffect(
    useCallback(async () => {
      await analyticsClient.sendDatabaseContentViewedEvent({
        contentId,
        databaseContainer: isStandalone()
          ? DatabaseContainerType.STANDALONE_DATABASE
          : DatabaseContainerType.EMBED_DATABASE,
        locale,
      });
    }, [analyticsClient, contentId, locale])
  );

  useCloseViewPanelKeyboardShortcut();

  return (
    <>
      <SetupPanelListener contentId={contentId} />
      <CopyListener contentId={contentId} />
      <EntryPanelLoader contentId={contentId} />
      <FilteredValuesLoader contentId={contentId} />
      <SpotlightManager blanketIsTinted={false}>
        <DatabaseLayout
          outerContainerRef={outerContainerRef}
          view={
            <DatabaseLayoutRenderer
              outerContainerRef={outerContainerRef}
              setOuterContainerRef={setOuterContainerRef}
              contentId={contentId}
              canEditContent={canEditContent}
              couldEditFields={couldEditFields}
              canEditFields={canEditFields}
              isContentEditable={isContentEditable}
              isFieldsEditable={isFieldsEditable}
            />
          }
          toolbar={
            <Toolbar
              contentId={contentId}
              actions={actions}
              canEditFields={canEditFields}
              canEditContent={canEditContent}
            />
          }
        />
      </SpotlightManager>
    </>
  );
};

export default DatabaseContent;
